/**
 * MemoryPicker - A reusable component for selecting or creating memories
 */

import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MemorySummary,
  MemoryCreate,
  listMemories,
  createMemory,
} from "../../api/memories";

interface MemoryPickerProps {
  value: number | null;
  onChange: (memoryId: number | null) => void;
  placeholder?: string;
  allowCreate?: boolean;
  className?: string;
}

export default function MemoryPicker({
  value,
  onChange,
  placeholder = "Select a memory...",
  allowCreate = true,
  className = "",
}: MemoryPickerProps) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMemory, setNewMemory] = useState<MemoryCreate>({ title: "" });

  // Fetch memories
  const { data: memories, isLoading } = useQuery({
    queryKey: ["memories-picker", search],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listMemories(token, { search: search || undefined, limit: 50 });
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: MemoryCreate) => {
      const token = await getAccessTokenSilently();
      return createMemory(token, data);
    },
    onSuccess: (newMem) => {
      queryClient.invalidateQueries({ queryKey: ["memories-picker"] });
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      onChange(newMem.id);
      setShowCreateForm(false);
      setNewMemory({ title: "" });
      setIsOpen(false);
    },
  });

  // Find selected memory
  const selectedMemory = memories?.find((mem) => mem.id === value);

  const handleSelect = (mem: MemorySummary) => {
    onChange(mem.id);
    setIsOpen(false);
    setSearch("");
  };

  const handleClear = () => {
    onChange(null);
  };

  const handleCreateSubmit = () => {
    if (newMemory.title.trim()) {
      createMutation.mutate(newMemory);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Selected value display / trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full rounded border border-vault-200 px-3 py-2 bg-white cursor-pointer hover:border-vault-300"
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedMemory ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span className="truncate text-vault-900">{selectedMemory.title}</span>
              {selectedMemory.memory_date && (
                <span className="text-vault-500 text-sm truncate">({selectedMemory.memory_date})</span>
              )}
            </>
          ) : (
            <span className="text-vault-400">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 text-vault-400 hover:text-vault-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-vault-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-vault-200 bg-white shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b border-vault-100">
            <input
              type="text"
              placeholder="Search memories..."
              className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          {/* Memory list */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="p-3 text-center text-vault-500 text-sm">Loading...</div>
            ) : memories && memories.length > 0 ? (
              memories.map((mem) => (
                <div
                  key={mem.id}
                  onClick={() => handleSelect(mem)}
                  className={`px-3 py-2 cursor-pointer hover:bg-vault-50 flex items-center gap-2 ${
                    mem.id === value ? "bg-vault-100" : ""
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <div className="min-w-0">
                    <div className="text-sm text-vault-900 truncate">{mem.title}</div>
                    {(mem.memory_date || mem.location_name) && (
                      <div className="text-xs text-vault-500 truncate">
                        {[mem.memory_date, mem.location_name].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-3 text-center text-vault-500 text-sm">
                {search ? "No memories found" : "No memories yet"}
              </div>
            )}
          </div>

          {/* Create new option */}
          {allowCreate && (
            <div className="border-t border-vault-100">
              {showCreateForm ? (
                <div className="p-3 space-y-2">
                  <input
                    type="text"
                    placeholder="Memory title (e.g., Christmas 1965)"
                    className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
                    value={newMemory.title}
                    onChange={(e) => setNewMemory({ ...newMemory, title: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Date (optional, e.g., Summer 1970)"
                    className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
                    value={newMemory.memory_date || ""}
                    onChange={(e) => setNewMemory({ ...newMemory, memory_date: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1 rounded bg-vault-100 px-3 py-1.5 text-sm text-vault-700 hover:bg-vault-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateSubmit}
                      disabled={!newMemory.title.trim() || createMutation.isPending}
                      className="flex-1 rounded bg-vault-600 px-3 py-1.5 text-sm text-white hover:bg-vault-700 disabled:opacity-50"
                    >
                      {createMutation.isPending ? "Creating..." : "Create"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full px-3 py-2 text-left text-sm text-vault-600 hover:bg-vault-50 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create new memory
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setShowCreateForm(false);
          }}
        />
      )}
    </div>
  );
}
